// eslint-disable-next-line @typescript-eslint/no-var-requires
/* eslint-disable @typescript-eslint/camelcase */
import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgeTokenApi } from '@trialcard/apigateway.client';
import { Authentication, LogoutOptions } from 'auth0-js';
import { AuthenticateStatus } from '@trialcard/enums';
import { Resolve } from 'vue-di';
import VueRouter from 'vue-router';
import moment, { Moment } from 'moment';
import { IAuthenticationData, ScriptsAuthenticationGetCurrentUserGetCurrentUserResponseModel } from '@trialcard/apigateway.models';
import {
    IdToken,
    GetTokenWithPopupOptions,
    PopupLoginOptions,
    RedirectLoginOptions,
    getIdTokenClaimsOptions,
    GetTokenSilentlyOptions,
} from '@auth0/auth0-spa-js';
import { OnClear } from '~/services/clearModule';
import { Auth0Client } from '~/services/authentication';
import SnackbarModule from '~/store/snackbar';

// TODO: THIS IS A TEMPORARY CLASS
// THIS CLASS MUST BE UPDATED TO GO TO UAT OR BEYOND
@InjectModule({ stateFactory: true }, module)
@OnClear<SessionModule>(async value => value.logout())
export default class SessionModule extends InjectVuexModule {
    @Resolve
    authentication!: Authentication;
    @Resolve
    edgeTokenApi!: EdgeTokenApi;
    @Resolve
    snackbar!: SnackbarModule;

    type: 'guest' | 'user' | null = null;
    features: string[] = [];
    sessionId: number | null = null;
    pending = false;
    isAuthenticated = false;
    popupOpen = false;
    error = '';
    email = '';
    email_verified = false;
    name = '';
    nickname = '';
    picture = '';
    sub = '';
    updated_at = '';
    aud = '';
    exp: number | null = null;
    iat: number | null = null;
    iss = '';
    token = '';
    profile: Auth0UserProfile | null = null;
    isMiniVariant = false;
    panel = [];
    panelIndex = {};
    medicinePanel = null;
    public 'https://apigateway.trialcard.com/portalId' = '';
    public 'https://apigateway.trialcard.com/roles': string[] = [];
    public 'https://apigateway.trialcard.com/account': number | null = null;
    public __raw = '';
    public nonce = '';
    public given_name = '';
    public family_name = '';

    passwordExpired = false;

    auth0Client() {
        return this.container.get<Auth0Client>(Auth0Client);
    }
    @Resolve
    router!: VueRouter;

    get isAuthenticatedUser() {
        return this.isAuthenticated;
    }

    @Action({})
    async ensure() {
        if (window.location.search.includes('Your%20password%20has%20expired')) {
            this.lockDueToExpiredPassword();
        } else {
            try {
                // If the user is returning to the app after authentication..
                if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                    // handle the redirect and retrieve tokens
                    const { appState } = await this.auth0Client().handleRedirectCallback();
                    // Notify subscribers that the redirect callback has happened, passing the appState
                    // (useful for retrieving any pre-authentication state)
                    window.history.replaceState({}, document.title, window.location.pathname);
                    // this.router.push(appState && appState.targetUrl ? appState.targetUrl : window.location.pathname);
                }
            } catch (error) {
                this.setError(error);
            } finally {
                // Conditionally calling guestAuth(enrollment) else Auth0(updateData->Auth)
                if (
                    (window.location.pathname.includes('enrollment') ||
                        window.location.pathname.includes('provider') ||
                        window.location.pathname.includes('success')) &&
                    !this.token
                ) {
                    const response = await this.edgeTokenApi.authenticationGuest({
                        key: process.env.PORTALID!,
                    });
                    this.setToken(response.data.data?.token!);
                } else {
                    // Initialize our internal authentication state
                    await this.updateData();
                }
                this.setPending(false);
            }
        }
    }

    @Action({})
    async updateData() {
        if (this.isAuthenticated) {
            const tokenUpdateRequired = !this.token || this["https://apigateway.trialcard.com/portalId"] !== process.env.PORTALID!
            const isTokenExpired = !!this.exp && (Math.round(Date.now() / 1000) > Math.round(this.exp!));
            // Refresh the token BEFORE using it for userInfo. Previously userInfo was
            // called with a possibly-expired token, 401'd, and the rejection propagated
            // unhandled (surfacing as a misleading vuex-module-decorators
            // ERR_ACTION_ACCESS_UNDEFINED error). updateToken() redirects to login on
            // login_required.
            if (tokenUpdateRequired || isTokenExpired) await this.updateToken();
            if (this.token && !this.profile) {
                const getProfile = (token: string) =>
                    new Promise<Auth0UserProfile>((resolve, reject) => {
                        this.authentication.userInfo(token, (e, profile) => {
                            if (e) {
                                reject(e);
                            } else {
                                resolve(profile);
                            }
                        });
                    });
                try {
                    this.setProfile(await getProfile(this.token));
                } catch (error) {
                    // Token was rejected by userInfo (e.g. 401) despite the refresh
                    // above. Refresh once more and retry; if the session is truly gone,
                    // clear it and redirect to login (same behavior as updateToken).
                    await this.updateToken();
                    try {
                        if (this.token) {
                            this.setProfile(await getProfile(this.token));
                        }
                    } catch (retryError) {
                        this.clearData();
                        await this.auth0Client().loginWithRedirect({ scope: 'openId' });
                    }
                }
            }
            if (this.token && isTokenExpired) {
                this.setData({
                    isAuthenticated: await this.auth0Client().isAuthenticated(),
                    user: await this.auth0Client().getUser(),
                    claims: await this.auth0Client().getIdTokenClaims() as IdToken,
                });

            }

        } else {
            this.setData({
                isAuthenticated: await this.auth0Client().isAuthenticated(),
                user: await this.auth0Client().getUser(),
                claims: await this.auth0Client().getIdTokenClaims() as IdToken,
            });
        }
    }

    @Action({})
    async updateToken() {
        let token = '';
        try {
            token = await this.auth0Client().getTokenSilently({scope: 'openId'});
        } catch (error) {
            if (error && error.error && error.error === 'login_required') {
                this.clearData();
                await this.auth0Client().loginWithRedirect({scope: 'openId'});
            }
        } finally {
            this.setToken(token);
        }
    }

    @Action({})
    public async guestAuthentication() {
        const response = await this.edgeTokenApi.authenticationGuest({
            key: process.env.PORTALID!,
        });
        this.setGuestData(response.data.data!);
    }

    @Mutation
    public setGuestData(data: IAuthenticationData) {
        this.type = 'guest';
        this.token = data.token!;
        this.sessionId = data.sessionId || null;
        this.features = data.features || [];
    }

    @Mutation
    setPanel(value: any) {
        this.panel = value;
    }

    @Mutation
    setPanelIndex(value: any) {
        this.panelIndex = value;
    }

    @Mutation
    setMedicinePanel(value: any) {
        this.medicinePanel = value;
    }

    @Mutation
    setData({ isAuthenticated, user, claims }: { isAuthenticated: boolean; user: any; claims: IdToken }) {
        this.isAuthenticated = isAuthenticated;
        Object.assign(this, user);
        Object.assign(this, claims);
    }
    @Mutation
    setToken(token: string) {
        this.token = token;
    }
    @Mutation
    setError(error: string) {
        this.error = error;
        this.isAuthenticated = false;
    }
    @Mutation
    toggleMiniVariant() {
        this.isMiniVariant = !this.isMiniVariant;
    }
    @Action({})
    async login() {
        this.loginWithRedirect();
    }
    @Mutation
    public clearData() {
        this.type = null;
        this.features = [];
        this.sessionId = null;
        this.isMiniVariant = false;
        this.pending = false;
        this.isAuthenticated = false;
        this.popupOpen = false;
        this.error = '';
        this.email = '';
        this.email_verified = false;
        this.name = '';
        this.nickname = '';
        this.picture = '';
        this.sub = '';
        this.updated_at = '';
        this.aud = '';
        this.exp = null;
        this.iat = null;
        this.iss = '';
        this.token = '';
        this.profile = null;
        this['https://apigateway.trialcard.com/portalId'] = '';
        this['https://apigateway.trialcard.com/roles'] = [];
        this['https://apigateway.trialcard.com/account'] = null;
        this.__raw = '';
        this.nonce = '';
        this.given_name = '';
        this.family_name = '';
        this.passwordExpired = false;
    }
    @Mutation
    lockDueToExpiredPassword(){
        this.passwordExpired = true;
    }
    @Mutation
    setPending(value: boolean) {
        this.pending = value;
    }

    @Mutation
    setProfile(profile: Auth0UserProfile) {
        this.profile = profile;
    }

    @Action({})
    async loginWithPopup(o?: PopupLoginOptions) {
        this.popupOpen = true;
        try {
            await this.auth0Client().loginWithPopup(o);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            this.popupOpen = false;
        }
        await this.updateData();
    }
    @Action({})
    /** Handles the callback when logging in using a redirect */
    async handleRedirectCallback() {
        this.pending = true;
        try {
            await this.auth0Client().handleRedirectCallback();
            await this.updateData();
        } catch (error) {
            this.error = error;
        } finally {
            this.pending = false;
        }
    }
    @Action({})
    /** Authenticates the user using the redirect method */
    loginWithRedirect(o?: RedirectLoginOptions) {
        return this.auth0Client().loginWithRedirect(o);
    }
    @Action({})
    /** Returns all the claims present in the ID token */
    getIdTokenClaims(o?: getIdTokenClaimsOptions) {
        return this.auth0Client().getIdTokenClaims(o);
    }
    @Action({})
    /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
    getTokenSilently(o?: GetTokenSilentlyOptions) {
        return this.auth0Client().getTokenSilently(o);
    }
    @Action({})
    /** Gets the access token using a popup window */
    getTokenWithPopup(o?: GetTokenWithPopupOptions) {
        return this.auth0Client().getTokenWithPopup(o);
    }
    /** Logs the user out and removes their session on the authorization server */
    @Action({})
    logout(o?: LogoutOptions) {
        this.clearData();
        return this.auth0Client().logout(o);
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface Auth0UserProfile {
    name: string;
    nickname: string;
    picture: string;
    user_id: string;
    username?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    email_verified?: boolean;
    clientID: string;
    gender?: string;
    locale?: string;
    identities: Auth0Identity[];
    created_at: string;
    updated_at: string;
    sub: string;
    user_metadata?: any;
    app_metadata?: any;
}
export interface Auth0Identity {
    connection: string;
    isSocial: boolean;
    provider: string;
    user_id: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
